import { Injectable, OnDestroy } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

/** Délai max avant déblocage forcé de l'UI. */
const LOAD_SAFETY_MS = 6000;
/** Délai sans requête HTTP après affichage de la route. */
const AUTO_COMPLETE_MS = 400;

/**
 * Logique commune de la barre de progression de navigation (chargement d'une page).
 *
 * Émet `loading$` / `progress$` en combinant les événements du routeur (NavigationStart/End)
 * et le nombre de requêtes HTTP de chargement initial d'une page (via l'intercepteur associé).
 *
 * Les classes dérivées décident, via {@link shouldTrackUrl}, quelles URLs déclenchent la barre.
 */
@Injectable()
export abstract class NavProgressBaseService implements OnDestroy {
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  private readonly _progress$ = new BehaviorSubject<number>(0);

  private routeReady = false;
  private pageLoadWindow = false;
  private dataPending = 0;
  private navTimer: ReturnType<typeof setInterval> | null = null;
  private completeTimer: ReturnType<typeof setTimeout> | null = null;
  private safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private activeNavigationId: number | null = null;
  private pendingTargetUrl = '';

  readonly loading$ = this._loading$.asObservable();
  readonly progress$ = this._progress$.asObservable();

  protected constructor(protected readonly router: Router) {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        const target = this.normalizeUrl(e.url);
        const current = this.normalizeUrl(this.router.url);
        const initialLoad = e.id === 1 && this.shouldTrackUrl(target);

        if (this.shouldTrackUrl(target) && (target !== current || initialLoad)) {
          this.pendingTargetUrl = target;
          this.activeNavigationId = e.id;
          this.startNavigation();
        }
        return;
      }

      if (
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError
      ) {
        if (!this.shouldFinishNavigation(e)) {
          return;
        }
        this.activeNavigationId = null;
        this.finishNavigation();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearNavTimer();
    this.clearCompleteTimer();
    this.clearSafetyTimer();
  }

  /** Détermine si une URL déclenche la barre de progression (à implémenter par la classe dérivée). */
  protected abstract shouldTrackUrl(url: string): boolean;

  shouldTrackHttpRequest(): boolean {
    const activeUrl = this.normalizeUrl(this.router.url || this.pendingTargetUrl);
    return this.pageLoadWindow && this.shouldTrackUrl(activeUrl);
  }

  wrapPageLoad<T>(source: Observable<T>): Observable<T> {
    this.beginPageDataLoad();
    return source.pipe(finalize(() => this.endPageDataLoad()));
  }

  markPageReady(): void {
    if (!this._loading$.value && !this.pageLoadWindow) {
      return;
    }
    this.routeReady = true;
    this.dataPending = 0;
    this.completeProgress();
  }

  beginPageDataLoad(): void {
    if (!this.pageLoadWindow) {
      return;
    }
    this.clearCompleteTimer();
    this.dataPending += 1;
    this.setProgress(Math.max(this._progress$.value, 58));
    this.syncLoadingState();
  }

  endPageDataLoad(): void {
    if (!this.pageLoadWindow) {
      return;
    }
    this.dataPending = Math.max(0, this.dataPending - 1);
    if (this.routeReady && this.dataPending === 0) {
      this.completeProgress();
      return;
    }
    if (this.routeReady && this.dataPending > 0) {
      this.scheduleAutoComplete();
    }
  }

  private shouldFinishNavigation(
    e: NavigationEnd | NavigationCancel | NavigationError
  ): boolean {
    if (this.activeNavigationId != null && e.id === this.activeNavigationId) {
      return true;
    }
    if (!(e instanceof NavigationEnd) || !this._loading$.value) {
      return false;
    }
    const url = this.normalizeUrl(e.urlAfterRedirects || e.url);
    if (!this.shouldTrackUrl(url)) {
      return false;
    }
    const pending = this.pendingTargetUrl;
    if (!pending) {
      return this.activeNavigationId != null;
    }
    return (
      url === pending ||
      url.startsWith(`${pending}/`) ||
      pending.startsWith(`${url}/`)
    );
  }

  private startNavigation(): void {
    this.routeReady = false;
    this.pageLoadWindow = true;
    this.dataPending = 0;
    this.clearCompleteTimer();
    this.clearSafetyTimer();
    this.setProgress(8);
    this.syncLoadingState(true);

    this.clearNavTimer();
    this.navTimer = setInterval(() => {
      const current = this._progress$.value;
      if (current < 42) {
        this.setProgress(current + 4);
      }
    }, 80);

    this.safetyTimer = setTimeout(() => this.forceComplete(), LOAD_SAFETY_MS);
  }

  private finishNavigation(): void {
    this.routeReady = true;
    this.pageLoadWindow = true;
    this.clearNavTimer();
    this.setProgress(Math.max(this._progress$.value, 48));

    if (this.dataPending === 0) {
      this.scheduleAutoComplete();
    }
  }

  private scheduleAutoComplete(): void {
    this.clearCompleteTimer();
    this.completeTimer = setTimeout(() => {
      if (this.pageLoadWindow && this.routeReady && this.dataPending === 0) {
        this.completeProgress();
      }
    }, AUTO_COMPLETE_MS);
  }

  private forceComplete(): void {
    if (!this._loading$.value && !this.pageLoadWindow) {
      return;
    }
    this.dataPending = 0;
    this.routeReady = true;
    this.completeProgress();
  }

  private completeProgress(): void {
    this.pageLoadWindow = false;
    this.pendingTargetUrl = '';
    this.activeNavigationId = null;
    this.clearNavTimer();
    this.clearCompleteTimer();
    this.clearSafetyTimer();
    this.setProgress(100);

    this.completeTimer = setTimeout(() => {
      this._loading$.next(false);
      this.setProgress(0);
      this.routeReady = false;
      this.completeTimer = null;
    }, 280);
  }

  private syncLoadingState(force = false): void {
    if (force || this._progress$.value > 0) {
      this._loading$.next(true);
    }
  }

  private setProgress(value: number): void {
    this._progress$.next(Math.min(100, Math.max(0, value)));
  }

  private normalizeUrl(url: string): string {
    return normalizePath(url);
  }

  private clearNavTimer(): void {
    if (this.navTimer) {
      clearInterval(this.navTimer);
      this.navTimer = null;
    }
  }

  private clearCompleteTimer(): void {
    if (this.completeTimer) {
      clearTimeout(this.completeTimer);
      this.completeTimer = null;
    }
  }

  private clearSafetyTimer(): void {
    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }
  }
}

export function normalizePath(url: string): string {
  return (url || '').split('?')[0].split('#')[0];
}
